'use client';

import { t } from '@lingui/macro';
import { useLingui } from '@lingui/react';
import { CircularProgress, TextField } from '@mui/material';
import { createFilterOptions } from '@mui/material/Autocomplete';
import { Autocomplete } from '@prismo-io/design-system';
import { Application, translate } from '@prismo-io/router';
import { LanguageCode, type LanguageCodeT } from '@prismo-io/schemas';
import { useRouter } from 'next/navigation';
import { useState } from 'react';
import { type Control, Controller, type FieldValues, type Path } from 'react-hook-form';
import useDebounceValue from '../../helpers/use-debounce';
import { useSearchUsersAutocomplete } from './use-search-users-autocomplete';
const buildUserUrl = (locale: LanguageCodeT, userUrl: string) => {
  // eslint-disable-next-line no-constant-condition, prettier/prettier
  return translate({
    app: Application.APP,
    path: `/profil/:user`,
    params: {
      user: userUrl
    },
    locale: locale as LanguageCodeT
  });
};
export type SearchUsersAutocompleteInputProps<F extends FieldValues = FieldValues> = {
  name: Path<F>;
  control: Control<F>;
  required?: boolean;
  label?: string;
  helperText?: string;
  disabled?: boolean;
};
export function SearchUsersAutocompleteInput<TFieldValues extends FieldValues>(props: SearchUsersAutocompleteInputProps<TFieldValues>) {
  const {
    name,
    control,
    required = false,
    label = undefined,
    helperText = undefined,
    disabled = false
  } = props;
  const {
    i18n
  } = useLingui();
  const {
    locale = LanguageCode.Enum.fr
  } = i18n;
  const router = useRouter();
  const [isOpen, setIsOpen] = useState(false);
  const [inputTerm, setInputTerm] = useState<string | null>(null);
  const {
    data: options = [],
    isLoading
  } = useSearchUsersAutocomplete({
    isOpen,
    term: inputTerm
  });
  const [option] = options;
  type Option = typeof option;
  const isLoadingDebounced = useDebounceValue(isLoading, 400);
  const filter = createFilterOptions<Option>();
  const onGoToUser = (userUrl: string) => {
    const user = buildUserUrl(locale as LanguageCodeT, userUrl);
    router.push(user);
  };
  return <Controller name={name} control={control} render={({
    field: {
      onChange,
      onBlur,
      value
    },
    fieldState: {
      error
    }
  }) => {
    return <Autocomplete multiple={false} freeSolo clearOnBlur clearOnEscape handleHomeEndKeys open={isOpen} onOpen={() => setIsOpen(true)} onClose={() => setIsOpen(false)} value={value} loading={isLoadingDebounced} loadingText={t`Recherchez un profil`} disabled={disabled} noOptionsText={t`Aucun résultat`} options={options} filterOptions={(options, params) => {
      const filtered: Option[] = filter(options, params);
      return filtered;
    }} getOptionLabel={opt => typeof opt === 'string' ? opt : opt ? `${opt.full_name}` : 'John Doe'} onInputChange={(e, term) => {
      setInputTerm(term);
    }} onChange={(event, value, reason, details) => {
      onChange(value);
    }} onBlur={event => {
      onBlur();
    }} isOptionEqualToValue={(option, value) => {
      return value ? option.id === (value?.id ?? value) : false;
    }} renderOption={(props, option) => {
      return <li {...props} key={option.id} onClick={() => onGoToUser(option.url)}>
                  {option.full_name}
                </li>;
    }} renderInput={params => <TextField label={label} {...params} required={required} error={!!error} InputLabelProps={{
      ...params.InputLabelProps,
      required
    }} InputProps={{
      ...params.InputProps,
      endAdornment: <>
                      {isLoading ? <CircularProgress color="inherit" size={20} /> : null}
                      {params.InputProps.endAdornment}
                    </>
    }} inputProps={{
      ...params.inputProps
    }} helperText={error ? error.message : helperText} />} />;
  }} data-sentry-element="Controller" data-sentry-component="SearchUsersAutocompleteInput" data-sentry-source-file="search-users-autocomplete.tsx" />;
}